import React, {useCallback, useMemo} from 'react';
import {debounce, sortBy} from 'lodash-es';
import {gql, useLazyQuery} from '@apollo/client';
import AutoField, {ActionType, SelectAutocompleteField} from '../AutoForm/AutoFieldNew';
import {
  SearchAccounts,
  SearchAccountsVariables,
  SearchAccounts_accounts,
} from '../../schema-types/SearchAccounts';
import {useAutoForm} from '../AutoForm/useAutoForm';
import {ACCOUNT_BASIC_FRAGMENT} from '../../graphql/fragments';

type accountSearchSharedProps = {
  searchField: SelectAutocompleteField;
  searchLoading: boolean;
  account: SearchAccounts_accounts | undefined;
};

export const useAccountSearch = (valueKey: string): accountSearchSharedProps => {
  const {values} = useAutoForm<any>();
  const [onSearchAccounts, {data, loading: searchLoading, error}] = useLazyQuery<
    SearchAccounts,
    SearchAccountsVariables
  >(
    gql`
      query SearchAccounts($search: String, $curoIds: [UUID!]) {
        accounts(search: $search, curoIds: $curoIds) {
          ...AccountBasicFragment
        }
      }
      ${ACCOUNT_BASIC_FRAGMENT}
    `
  );
  const accounts = useMemo(() => {
    return data?.accounts || [];
  }, [data?.accounts]);
  const debounceSearch = useCallback(debounce(onSearchAccounts, 1000), []);
  const searchField = useMemo(() => {
    const field: SelectAutocompleteField = {
      id: valueKey,
      type: ActionType.selectAutocomplete,
      serverSearch: {
        fn: async (term) => {
          if (term?.length > 3) {
            debounceSearch({variables: {search: term}});
          }
        },
        inputFormKey: 'searchTerm',
      },
      searchIcon: true,
      label: 'Search account',
      initialValue: '',
      loading: searchLoading,
      options: sortBy(accounts, 'businessUnit.name').map((acc) => ({
        value: acc.id,
        label: acc.name || 'Unnamed Account',
        group: acc?.businessUnit
          ? `${acc?.businessUnit?.name} (${acc.businessUnit?.partnershipType})`
          : 'No business unit',
      })),
    };
    return field;
  }, [accounts, searchLoading, debounceSearch]);

  const account = useMemo(
    () => accounts.find((x) => x.id === values[valueKey]),
    [accounts, values[valueKey]]
  );
  return {
    account,
    searchField,
    searchLoading,
  };
};

export const AccountSearchInput: React.FC<accountSearchSharedProps> = ({
  searchField,
  searchLoading,
  account,
}) => {
  return (
    <AutoField loading={searchLoading} field={searchField} data-testid="account-select" />
  );
};
